import { useEffect, useState } from 'react';
import { patientPortal } from '@/api/patientPortal';
import PatientPortalPageHero from './PatientPortalPageHero';

type Doctor = { id: string; name: string; specialisation: string | null };
type Slot = { time: string; datetime: string };

export default function PatientPortalBooking() {
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [doctorId, setDoctorId] = useState('');
  const [date, setDate] = useState('');
  const [slots, setSlots] = useState<Slot[]>([]);
  const [selected, setSelected] = useState('');
  const [msg, setMsg] = useState('');

  useEffect(() => {
    patientPortal.getBookingDoctors().then(setDoctors).catch(() => {});
  }, []);

  useEffect(() => {
    if (!doctorId || !date) {
      setSlots([]);
      return;
    }
    patientPortal
      .getBookingSlots(doctorId, date)
      .then((d: { slots: Slot[] }) => setSlots(d.slots ?? []))
      .catch(() => setSlots([]));
  }, [doctorId, date]);

  const book = async () => {
    setMsg('');
    if (!doctorId || !selected) {
      setMsg('Select doctor, date, and time slot');
      return;
    }
    try {
      await patientPortal.bookAppointment(doctorId, selected);
      setMsg('Appointment booked successfully!');
      setSelected('');
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      setMsg(err.response?.data?.message ?? 'Booking failed');
    }
  };

  return (
    <div className="space-y-6 max-w-lg">
      <PatientPortalPageHero
        imageKey="booking"
        eyebrow="Appointments"
        title="Book OPD visit"
        subtitle="Choose your doctor, date, and time — we will confirm your slot"
        compact
      />
      <p className="text-sm text-muted-foreground">
        Book directly with your hospital OPD schedule — same slots as reception.
      </p>

      <label className="block text-sm">
        Doctor
        <select
          className="mt-1 w-full border rounded-lg px-3 py-2"
          value={doctorId}
          onChange={(e) => setDoctorId(e.target.value)}
        >
          <option value="">Select doctor</option>
          {doctors.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name}
              {d.specialisation ? ` — ${d.specialisation}` : ''}
            </option>
          ))}
        </select>
      </label>

      <label className="block text-sm">
        Date
        <input
          type="date"
          className="mt-1 w-full border rounded-lg px-3 py-2"
          value={date}
          min={new Date().toISOString().slice(0, 10)}
          onChange={(e) => setDate(e.target.value)}
        />
      </label>

      {slots.length > 0 && (
        <div>
          <p className="text-sm font-medium mb-2">Available slots</p>
          <div className="flex flex-wrap gap-2">
            {slots.map((s) => (
              <button
                key={s.datetime}
                type="button"
                onClick={() => setSelected(s.datetime)}
                className={`px-3 py-2 rounded-lg border text-sm ${
                  selected === s.datetime
                    ? 'bg-teal-600 text-white border-teal-600'
                    : 'bg-white border-slate-200'
                }`}
              >
                {s.time}
              </button>
            ))}
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={book}
        className="w-full bg-teal-600 text-white rounded-lg py-3 font-medium"
      >
        Confirm booking
      </button>
      {msg ? <p className="text-sm">{msg}</p> : null}
    </div>
  );
}
